import { useEffect, useState } from "react";
import { api, fmtBRL, fmtDate } from "@/lib/api";
import { fmtRua, extractError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Link } from "react-router-dom";
import { Building2, ArrowUpRight, Pencil, Zap, FileText, Ruler, Tag, Plus, Trash2, Search, ArrowUp, ArrowDown, History } from "lucide-react";
import { toast } from "sonner";

const statusCores = {
  OCUPADA: "text-success border-success/40 bg-success/5",
  LIVRE: "text-muted-foreground border-border bg-background",
  OBRA: "text-warning border-warning/40 bg-warning/5",
};

export default function AdminUnidades() {
  const [unidades, setUnidades] = useState([]);
  const [filtroStatus, setFiltroStatus] = useState("TODAS");
  const [filtroTipo, setFiltroTipo] = useState("TODOS");
  const [filtroFrente, setFiltroFrente] = useState("TODAS");
  const [filtroPavimento, setFiltroPavimento] = useState("TODOS");
  const [busca, setBusca] = useState("");
  const [agruparPor, setAgruparPor] = useState("nenhum"); // nenhum | pavimento | tipo
  const [editOpen, setEditOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({});
  const [newOpen, setNewOpen] = useState(false);
  const [newForm, setNewForm] = useState({
    numero_completo: "",
    tipo: "LOJA",
    metragem: "",
    pavimento: "TERREO",
    frente: "INTERNA",
    apelido: "",
    iptu_valor_mensal: "",
  });
  const [historicoOpen, setHistoricoOpen] = useState(false);
  const [historicoUnidade, setHistoricoUnidade] = useState(null);
  const [historicoData, setHistoricoData] = useState({ ocupantes: [], total: 0 });
  const [historicoLoading, setHistoricoLoading] = useState(false);

  const abrirHistorico = async (u) => {
    setHistoricoUnidade(u);
    setHistoricoOpen(true);
    setHistoricoLoading(true);
    setHistoricoData({ ocupantes: [], total: 0 });
    try {
      const { data } = await api.get(`/unidades/${u.id}/historico-ocupacao`);
      setHistoricoData({ ocupantes: data.ocupantes || [], total: data.total || 0 });
    } catch (e) {
      toast.error(extractError(e) || "Falha ao carregar histórico");
    } finally {
      setHistoricoLoading(false);
    }
  };

  const load = async () => {
    const { data } = await api.get("/unidades");
    setUnidades(data);
  };
  useEffect(() => { load(); }, []);

  const openEdit = (u) => {
    setSelected(u);
    setForm({
      apelido: u.apelido || "",
      metragem: u.metragem,
      pavimento: u.pavimento,
      frente: u.frente,
      status: u.status,
      numero_iptu: u.numero_iptu || "",
      iptu_valor_anual: u.iptu_valor_anual ?? "",
      iptu_valor_mensal: u.iptu_valor_mensal ?? "",
      numero_medidor_energia: u.numero_medidor_energia || "",
      tipo_porta: u.tipo_porta || "",
      instalacoes: u.instalacoes || "",
      observacoes: u.observacoes || "",
    });
    setEditOpen(true);
  };

  const salvar = async () => {
    // Sanitização robusta — nunca deixa string vazia virar NaN
    const safeNum = (v) => {
      if (v === "" || v === null || v === undefined) return undefined;
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : undefined;
    };
    try {
      const payload = {
        ...form,
        metragem: safeNum(form.metragem),
        iptu_valor_anual: safeNum(form.iptu_valor_anual),
        iptu_valor_mensal: safeNum(form.iptu_valor_mensal),
      };
      // Remove chaves undefined para não sobrescrever no backend
      Object.keys(payload).forEach((k) => {
        if (payload[k] === undefined) delete payload[k];
      });
      await api.patch(`/unidades/${selected.id}`, payload);
      toast.success("Unidade atualizada");
      setEditOpen(false);
      load();
    } catch (e) {
      toast.error(extractError(e) || "Falha ao salvar");
    }
  };

  const updateStatusQuick = async (id, status) => {
    await api.patch(`/unidades/${id}`, { status });
    load();
  };

  const criarUnidade = async () => {
    const idfmt = String(newForm.numero_completo || "").trim().toUpperCase();
    if (!idfmt) return toast.error("Identificador obrigatório (ex: LOJA-201)");
    const metragem = parseFloat(newForm.metragem);
    if (!Number.isFinite(metragem) || metragem <= 0) return toast.error("Metragem inválida");
    try {
      await api.post("/unidades", {
        numero_completo: idfmt,
        tipo: newForm.tipo,
        metragem,
        pavimento: newForm.pavimento,
        frente: newForm.frente,
        apelido: newForm.apelido?.trim() || undefined,
        iptu_valor_mensal: parseFloat(newForm.iptu_valor_mensal) || 0,
      });
      toast.success(`Unidade ${idfmt} criada`);
      setNewOpen(false);
      setNewForm({ numero_completo: "", tipo: "LOJA", metragem: "", pavimento: "TERREO", frente: "INTERNA", apelido: "", iptu_valor_mensal: "" });
      load();
    } catch (e) {
      toast.error(extractError(e) || "Falha ao criar unidade");
    }
  };

  const excluirUnidade = async (u) => {
    const alvo = `${u.numero_completo}${u.apelido ? " (" + u.apelido + ")" : ""}`;
    if (u.status === "OCUPADA") {
      return toast.error("Unidade ocupada — encerre o contrato antes de excluir");
    }
    if (!window.confirm(`Excluir a unidade ${alvo}?\n\nEsta ação é permanente e não pode ser desfeita.`)) return;
    try {
      await api.delete(`/unidades/${u.id}`);
      toast.success(`Unidade ${alvo} excluída`);
      load();
    } catch (e) {
      toast.error(extractError(e) || "Falha ao excluir unidade");
    }
  };

  const buscaNormalizada = busca.trim().toLowerCase();
  const filtered = unidades.filter((u) => {
    if (filtroStatus !== "TODAS" && u.status !== filtroStatus) return false;
    if (filtroTipo !== "TODOS" && u.tipo !== filtroTipo) return false;
    if (filtroFrente !== "TODAS" && u.frente !== filtroFrente) return false;
    if (filtroPavimento !== "TODOS" && u.pavimento !== filtroPavimento) return false;
    if (buscaNormalizada) {
      const alvo = [
        u.numero_completo,
        u.apelido,
        u.tipo,
        u.contrato?.razao_social,
        u.contrato?.responsavel,
        u.contrato?.cnpj,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      if (!alvo.includes(buscaNormalizada)) return false;
    }
    return true;
  });

  // Agrupamento (por pavimento ou tipo)
  const agrupar = (lista) => {
    if (agruparPor === "nenhum") return { "": lista };
    return lista.reduce((acc, u) => {
      const key = agruparPor === "pavimento" ? (u.pavimento || "SEM PAVIMENTO") : (u.tipo || "SEM TIPO");
      acc[key] = acc[key] || [];
      acc[key].push(u);
      return acc;
    }, {});
  };
  const grupos = agrupar(filtered);

  const moverUnidade = async (u, direction) => {
    try {
      const { data } = await api.post(`/unidades/${u.id}/mover?direction=${direction}`);
      if (data?.ok === false) {
        toast.info(data.reason || "Sem movimento");
      } else {
        load();
      }
    } catch (e) {
      toast.error(extractError(e) || "Falha ao mover unidade");
    }
  };

  const totais = {
    total: unidades.length,
    ocupadas: unidades.filter((u) => u.status === "OCUPADA").length,
    livres: unidades.filter((u) => u.status === "LIVRE").length,
    obra: unidades.filter((u) => u.status === "OBRA").length,
  };

  return (
    <div className="space-y-8 fade-up">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-tag mb-2">Ficha detalhada · IPTU · Medidor · Instalações</div>
          <h1 className="font-heading font-black tracking-tighter text-4xl sm:text-5xl">Unidades</h1>
        </div>
        <Button
          data-testid="btn-nova-unidade"
          onClick={() => setNewOpen(true)}
          className="rounded-sm bg-foreground text-background hover:bg-foreground/90"
        >
          <Plus size={14} className="mr-2" /> Nova Unidade
        </Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Total", v: totais.total },
          { label: "Ocupadas", v: totais.ocupadas, c: "text-success" },
          { label: "Livres", v: totais.livres },
          { label: "Em obra", v: totais.obra, c: "text-warning" },
        ].map((s) => (
          <div key={s.label} className="stat-block p-4 rounded-sm">
            <div className="label-tag">{s.label}</div>
            <div className={`stat-num text-3xl mt-2 ${s.c || ""}`}>{s.v}</div>
          </div>
        ))}
      </div>

      <div className="space-y-3">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="busca-unidade"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="🔎 Buscar por lojista, número ou nome da unidade..."
            className="pl-9 rounded-sm"
          />
        </div>

        <div className="flex flex-wrap gap-3">
          <Select value={filtroStatus} onValueChange={setFiltroStatus}>
            <SelectTrigger data-testid="filtro-status" className="w-full sm:w-40 rounded-sm"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="TODAS">Todos status</SelectItem>
              <SelectItem value="OCUPADA">Ocupada</SelectItem>
              <SelectItem value="LIVRE">Livre</SelectItem>
              <SelectItem value="OBRA">Em obra</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filtroTipo} onValueChange={setFiltroTipo}>
            <SelectTrigger data-testid="filtro-tipo" className="w-full sm:w-52 rounded-sm"><SelectValue placeholder="Tipo" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="TODOS">Todos os tipos</SelectItem>
              <SelectItem value="LOJA">Loja</SelectItem>
              <SelectItem value="ESTANDE">Estande</SelectItem>
              <SelectItem value="CONTAINER">Container</SelectItem>
              <SelectItem value="SOBRELOJA">Sobreloja</SelectItem>
              <SelectItem value="QUIOSQUE">Quiosque</SelectItem>
              <SelectItem value="ESTANDE_CONTAINER">Estande / Container</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filtroPavimento} onValueChange={setFiltroPavimento}>
            <SelectTrigger data-testid="filtro-pavimento" className="w-full sm:w-40 rounded-sm"><SelectValue placeholder="Pavimento" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="TODOS">Todos pavimentos</SelectItem>
              <SelectItem value="TERREO">Térreo</SelectItem>
              <SelectItem value="SUPERIOR">Superior / Sobreloja</SelectItem>
              <SelectItem value="EXTERNO">Externo</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filtroFrente} onValueChange={setFiltroFrente}>
            <SelectTrigger data-testid="filtro-frente" className="w-full sm:w-64 rounded-sm"><SelectValue placeholder="Frente" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="TODAS">Todas as ruas</SelectItem>
              <SelectItem value="RUA_A">R. Eduardina de M. Telles</SelectItem>
              <SelectItem value="RUA_B">R. Arthur R. Loivos</SelectItem>
            </SelectContent>
          </Select>
          <Select value={agruparPor} onValueChange={setAgruparPor}>
            <SelectTrigger data-testid="agrupar-por" className="w-full sm:w-44 rounded-sm"><SelectValue placeholder="Agrupar por" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="nenhum">Sem agrupamento</SelectItem>
              <SelectItem value="pavimento">Agrupar por pavimento</SelectItem>
              <SelectItem value="tipo">Agrupar por tipo</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="text-xs text-muted-foreground">
          Exibindo <b className="text-foreground">{filtered.length}</b> de <b className="text-foreground">{unidades.length}</b> unidades
        </div>
      </div>

      {Object.entries(grupos).map(([grupo, lista]) => (
        <div key={grupo || "todos"} className="space-y-3">
          {grupo && (
            <div className="flex items-center gap-2 border-b border-border pb-2 mt-6">
              <h3 className="font-heading font-bold text-lg tracking-tighter uppercase">{grupo}</h3>
              <Badge variant="outline" className="text-[10px]">{lista.length}</Badge>
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {lista.map((u) => (
              <div
                key={u.id}
                data-testid={`unidade-card-${u.numero_completo}`}
                className={`border rounded-sm p-4 ${statusCores[u.status]} transition-transform duration-200 ease-out hover:-translate-y-[1px]`}
              >
                <div className="flex items-start justify-between mb-3">
                  <Building2 size={16} />
                  <Badge variant="outline" className="text-[9px] uppercase tracking-widest border-current">{u.status}</Badge>
                </div>
                <div className="font-heading font-black text-xl tracking-tighter break-anywhere">{u.apelido || u.numero_completo}</div>
                {u.apelido && (
                  <div className="text-[10px] font-mono text-muted-foreground truncate">{u.numero_completo}</div>
                )}
                <div className="text-[11px] text-muted-foreground font-mono mt-1">
                  {u.tipo === "LOJA" ? "Loja" : u.tipo === "ESTANDE" ? "Estande" : u.tipo === "CONTAINER" ? "Container" : u.tipo === "QUIOSQUE" ? "Quiosque" : u.tipo === "SOBRELOJA" ? "Sobreloja" : "Estande"} · {u.metragem}m²
                </div>
                <div className="text-[11px] text-muted-foreground font-mono truncate" title={fmtRua(u.frente)}>
                  {u.pavimento} · {fmtRua(u.frente, true)}
                </div>
                {u.contrato && (
                  <div className="text-[11px] mt-2 border-t border-current/20 pt-2">
                    <div className="font-semibold truncate" title={u.contrato.razao_social}>{u.contrato.razao_social}</div>
                    {u.contrato.responsavel && (
                      <div className="text-muted-foreground truncate text-[10px]">{u.contrato.responsavel}</div>
                    )}
                  </div>
                )}
                <div className="flex flex-wrap gap-1 mt-3">
                  <Button variant="outline" size="icon" onClick={() => openEdit(u)} data-testid={`edit-${u.numero_completo}`} className="h-7 w-7 rounded-sm" title="Editar ficha">
                    <Pencil size={12} />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => abrirHistorico(u)}
                    data-testid={`historico-${u.numero_completo}`}
                    className="h-7 w-7 rounded-sm border-info/40 text-info hover:bg-info/5"
                    title="Histórico de ocupantes desta unidade"
                  >
                    <History size={12} />
                  </Button>
                  <Select value={u.status} onValueChange={(v) => updateStatusQuick(u.id, v)}>
                    <SelectTrigger className="h-7 text-[10px] rounded-sm flex-1 min-w-[60px]" data-testid={`status-${u.numero_completo}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="LIVRE">Livre</SelectItem>
                      <SelectItem value="OCUPADA">Ocupada</SelectItem>
                      <SelectItem value="OBRA">Em obra</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => moverUnidade(u, "up")}
                    data-testid={`mover-up-${u.numero_completo}`}
                    className="h-7 w-7 rounded-sm"
                    title="Mover para cima"
                  >
                    <ArrowUp size={12} />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => moverUnidade(u, "down")}
                    data-testid={`mover-down-${u.numero_completo}`}
                    className="h-7 w-7 rounded-sm"
                    title="Mover para baixo"
                  >
                    <ArrowDown size={12} />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => excluirUnidade(u)}
                    data-testid={`excluir-${u.numero_completo}`}
                    className="h-7 w-7 rounded-sm border-destructive/40 text-destructive hover:bg-destructive/10"
                    title={u.status === "OCUPADA" ? "Unidade ocupada — encerre o contrato" : "Excluir unidade"}
                    disabled={u.status === "OCUPADA"}
                  >
                    <Trash2 size={12} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {filtered.length === 0 && (
        <div className="text-center py-16 text-muted-foreground text-sm border border-dashed rounded-sm">
          Nenhuma unidade corresponde aos filtros.
        </div>
      )}

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="rounded-sm max-w-3xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl tracking-tighter">
              Ficha da unidade — {selected?.apelido || selected?.numero_completo}
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[70vh] overflow-y-auto space-y-6">
            <section>
              <div className="flex items-center gap-2 mb-3">
                <Tag size={14} />
                <h3 className="label-tag">Identificação da unidade</h3>
              </div>
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <Label className="label-tag">Apelido / Rótulo customizado (opcional)</Label>
                  <Input
                    data-testid="edit-apelido"
                    value={form.apelido || ""}
                    onChange={(e) => setForm({ ...form, apelido: e.target.value })}
                    placeholder={`Ex.: Loja Âncora Frente · deixe vazio para usar ${selected?.numero_completo}`}
                  />
                  <div className="text-[11px] text-muted-foreground mt-1">
                    Identificador padrão gerado pelo sistema: <span className="font-mono">{selected?.numero_completo}</span>
                  </div>
                </div>
              </div>
            </section>

            <section>
              <div className="flex items-center gap-2 mb-3">
                <Ruler size={14} />
                <h3 className="label-tag">Dimensões & localização física</h3>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div>
                  <Label className="label-tag">Metragem (m²)</Label>
                  <Input data-testid="edit-metragem" type="number" step="0.01" value={form.metragem || ""} onChange={(e) => setForm({ ...form, metragem: e.target.value })} />
                </div>
                <div>
                  <Label className="label-tag">Pavimento</Label>
                  <Select value={form.pavimento} onValueChange={(v) => setForm({ ...form, pavimento: v })}>
                    <SelectTrigger data-testid="edit-pavimento"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="TERREO">Térreo</SelectItem>
                      <SelectItem value="SUPERIOR">Superior</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-tag">Fachada / Rua</Label>
                  <Select value={form.frente} onValueChange={(v) => setForm({ ...form, frente: v })}>
                    <SelectTrigger data-testid="edit-frente"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="RUA_A">R. Eduardina de M. Telles</SelectItem>
                      <SelectItem value="RUA_B">R. Arthur R. Loivos</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </section>

            <section>
              <div className="flex items-center gap-2 mb-3">
                <FileText size={14} />
                <h3 className="label-tag">Números fiscais / concessionárias</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="label-tag">Nº IPTU individual (inscrição municipal)</Label>
                  <Input data-testid="edit-iptu" value={form.numero_iptu || ""} onChange={(e) => setForm({ ...form, numero_iptu: e.target.value })} placeholder="Ex: 123.456.789-0" />
                </div>
                <div>
                  <Label className="label-tag flex items-center gap-1"><Zap size={10} /> Medidor de energia</Label>
                  <Input data-testid="edit-medidor" value={form.numero_medidor_energia || ""} onChange={(e) => setForm({ ...form, numero_medidor_energia: e.target.value })} placeholder="Ex: 987654321" />
                </div>
                <div>
                  <Label className="label-tag">Valor do IPTU anual (R$)</Label>
                  <Input
                    data-testid="edit-iptu-anual"
                    type="number"
                    step="0.01"
                    value={form.iptu_valor_anual ?? ""}
                    onChange={(e) => {
                      const raw = e.target.value;
                      const num = raw === "" ? "" : parseFloat(raw);
                      // Auto-calcula o mensal ao digitar o anual
                      const mensal = Number.isFinite(num) ? Number((num / 12).toFixed(2)) : "";
                      setForm({ ...form, iptu_valor_anual: raw, iptu_valor_mensal: mensal });
                    }}
                    placeholder="0,00"
                  />
                </div>
                <div>
                  <Label className="label-tag">Valor do IPTU mensal (R$) — auto</Label>
                  <Input
                    data-testid="edit-iptu-mensal"
                    type="number"
                    step="0.01"
                    value={form.iptu_valor_mensal ?? ""}
                    onChange={(e) => {
                      const raw = e.target.value;
                      const num = raw === "" ? "" : parseFloat(raw);
                      // Reflete no anual se admin editar o mensal manualmente
                      const anual = Number.isFinite(num) ? Number((num * 12).toFixed(2)) : "";
                      setForm({ ...form, iptu_valor_mensal: raw, iptu_valor_anual: anual });
                    }}
                    placeholder="0,00"
                  />
                  <div className="text-[10px] text-muted-foreground mt-1">
                    Este valor entra automaticamente na composição do boleto mensal desta unidade.
                  </div>
                </div>
              </div>
            </section>

            <section>
              <h3 className="label-tag mb-3">Instalações & infraestrutura</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="label-tag">Tipo de porta</Label>
                  <Input data-testid="edit-porta" value={form.tipo_porta || ""} onChange={(e) => setForm({ ...form, tipo_porta: e.target.value })} placeholder="Ex: Vidro temperado / Rolo metálica" />
                </div>
                <div>
                  <Label className="label-tag">Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger data-testid="edit-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="LIVRE">Livre</SelectItem>
                      <SelectItem value="OCUPADA">Ocupada</SelectItem>
                      <SelectItem value="OBRA">Em obra</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <Label className="label-tag">Resumo descritivo (hidráulica, elétrica, ar-condicionado, etc.)</Label>
                  <Textarea data-testid="edit-instalacoes" value={form.instalacoes || ""} onChange={(e) => setForm({ ...form, instalacoes: e.target.value })} rows={3} />
                </div>
                <div className="sm:col-span-2">
                  <Label className="label-tag">Observações</Label>
                  <Textarea data-testid="edit-observacoes" value={form.observacoes || ""} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} rows={2} />
                </div>
              </div>
            </section>

            {selected?.contrato && (
              <section className="border-t border-border pt-4">
                <h3 className="label-tag mb-2">Contrato vinculado</h3>
                <div className="text-sm">{selected.contrato.razao_social}</div>
                <div className="text-xs text-muted-foreground font-mono">{selected.contrato.cnpj}</div>
              </section>
            )}

            <Link to={`/admin/unidades/${selected?.id}/historico`} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="edit-ver-liquidez">
              Ver histórico completo & liquidez do ponto <ArrowUpRight size={14} />
            </Link>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancelar</Button>
            <Button data-testid="edit-salvar" onClick={salvar} className="bg-foreground text-background hover:bg-foreground/90">Salvar alterações</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal — Nova Unidade */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl tracking-tighter">Nova Unidade</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="col-span-1 sm:col-span-2">
              <Label className="label-tag">Identificador *</Label>
              <Input
                data-testid="new-numero"
                value={newForm.numero_completo}
                onChange={(e) => setNewForm({ ...newForm, numero_completo: e.target.value })}
                placeholder="Ex: LOJA-201 · ESTANDE-05 · QSK-02"
              />
            </div>
            <div className="col-span-1 sm:col-span-2">
              <Label className="label-tag">Apelido (opcional)</Label>
              <Input
                data-testid="new-apelido"
                value={newForm.apelido}
                onChange={(e) => setNewForm({ ...newForm, apelido: e.target.value })}
                placeholder="Nome amigável (ex: Farmácia da Praça)"
              />
            </div>
            <div>
              <Label className="label-tag">Tipo *</Label>
              <Select value={newForm.tipo} onValueChange={(v) => setNewForm({ ...newForm, tipo: v })}>
                <SelectTrigger data-testid="new-tipo" className="rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="LOJA">Loja</SelectItem>
                  <SelectItem value="ESTANDE">Estande</SelectItem>
                  <SelectItem value="CONTAINER">Container</SelectItem>
                  <SelectItem value="SOBRELOJA">Sobreloja</SelectItem>
                  <SelectItem value="QUIOSQUE">Quiosque</SelectItem>
                  <SelectItem value="ESTANDE_CONTAINER">Estande / Container</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-tag">Metragem (m²) *</Label>
              <Input
                data-testid="new-metragem"
                type="number"
                step="0.01"
                value={newForm.metragem}
                onChange={(e) => setNewForm({ ...newForm, metragem: e.target.value })}
              />
            </div>
            <div>
              <Label className="label-tag">Pavimento</Label>
              <Select value={newForm.pavimento} onValueChange={(v) => setNewForm({ ...newForm, pavimento: v })}>
                <SelectTrigger data-testid="new-pav" className="rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="TERREO">Térreo</SelectItem>
                  <SelectItem value="SOBRELOJA">Sobreloja</SelectItem>
                  <SelectItem value="EXTERNO">Externo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-tag">Frente</Label>
              <Select value={newForm.frente} onValueChange={(v) => setNewForm({ ...newForm, frente: v })}>
                <SelectTrigger data-testid="new-frente" className="rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="INTERNA">Interna</SelectItem>
                  <SelectItem value="RUA_A">R. Eduardina de M. Telles</SelectItem>
                  <SelectItem value="RUA_B">R. Arthur R. Loivos</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-1 sm:col-span-2">
              <Label className="label-tag">Valor referência IPTU mensal (R$)</Label>
              <Input
                data-testid="new-iptu"
                type="number"
                step="0.01"
                value={newForm.iptu_valor_mensal}
                onChange={(e) => setNewForm({ ...newForm, iptu_valor_mensal: e.target.value })}
                placeholder="0.00"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewOpen(false)}>Cancelar</Button>
            <Button data-testid="new-criar" onClick={criarUnidade} className="bg-foreground text-background hover:bg-foreground/90">Criar unidade</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal — Histórico de Ocupantes */}
      <Dialog open={historicoOpen} onOpenChange={setHistoricoOpen}>
        <DialogContent className="rounded-sm max-w-2xl" data-testid="modal-historico-ocupacao">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl tracking-tighter flex items-center gap-2">
              <History size={20} /> Histórico de ocupação — {historicoUnidade?.apelido || historicoUnidade?.numero_completo}
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[70vh] overflow-y-auto">
            {historicoLoading ? (
              <div className="text-center py-10 text-sm text-muted-foreground">Carregando timeline...</div>
            ) : historicoData.total === 0 ? (
              <div className="text-center py-10 text-sm text-muted-foreground border border-dashed rounded-sm">
                Nenhum ocupante registrado nesta unidade ainda.
              </div>
            ) : (
              <div className="space-y-4">
                <div className="text-xs text-muted-foreground">
                  <b className="text-foreground">{historicoData.total}</b> ocupante(s) registrado(s) — do mais recente para o mais antigo
                </div>
                <div className="relative pl-6 border-l-2 border-border">
                  {historicoData.ocupantes.map((oc, idx) => (
                    <div key={`${oc.contrato_id || "sem"}-${idx}`} data-testid={`ocupante-${idx}`} className="mb-6 relative">
                      <div className={`absolute -left-[31px] top-1 w-4 h-4 rounded-full border-2 ${oc.atual ? "bg-success border-success" : "bg-background border-muted-foreground"}`} />
                      <div className={`border rounded-sm p-4 ${oc.atual ? "border-success/40 bg-success/5" : "border-border"}`}>
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <span className="font-heading font-bold text-lg break-anywhere">{oc.razao_social || "—"}</span>
                          {oc.atual && (
                            <Badge className="uppercase tracking-widest text-[10px] bg-success text-background border-success">Ocupante atual</Badge>
                          )}
                          {!oc.atual && (
                            <Badge variant="outline" className="uppercase tracking-widest text-[10px] border-muted-foreground/40 text-muted-foreground">
                              {oc.status || "Encerrado"}
                            </Badge>
                          )}
                        </div>
                        {(oc.responsavel || oc.cnpj) && (
                          <div className="text-xs font-mono text-muted-foreground mb-2">
                            {oc.cnpj || ""}{oc.responsavel ? ` · ${oc.responsavel}` : ""}
                          </div>
                        )}
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
                          <div>
                            <div className="label-tag mb-1">Início</div>
                            <div className="font-mono">{oc.data_inicio ? fmtDate(oc.data_inicio) : "—"}</div>
                          </div>
                          <div>
                            <div className="label-tag mb-1">Término</div>
                            <div className="font-mono">{oc.data_termino ? fmtDate(oc.data_termino) : (oc.atual ? "Em curso" : "—")}</div>
                          </div>
                          <div>
                            <div className="label-tag mb-1">Aluguel praticado</div>
                            <div className="font-mono">{oc.valor_aluguel != null ? fmtBRL(oc.valor_aluguel) : "—"}</div>
                          </div>
                        </div>
                        {oc.motivo_encerramento && (
                          <div className="mt-3 pt-3 border-t border-border/60">
                            <div className="label-tag mb-1">Motivo do encerramento</div>
                            <div className="text-xs">{oc.motivo_encerramento}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoricoOpen(false)} data-testid="historico-fechar">Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
